import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  DIALOG_RETURN_TYPES,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, take, map } from 'rxjs';
import { Flock } from 'src/app/flock/models/flock.model';
import { getFlocks } from 'src/app/flock/store/selectors/flock.selectors';

import { DeliveryTableLine, FlockInfo } from '../../models/delivery.model';

export interface DeliveryDialogInputData extends AbstractFormDialogData {
  delivery: DeliveryTableLine;
  flockId: number;
}

export interface DeliveryDialogResultData {
  flock: FlockInfo;
}

@Component({
  selector: 'app-flock-delivery',
  templateUrl: './flock-delivery.component.html',
  styleUrls: ['./flock-delivery.component.scss'],
  standalone: false,
})
export class FlockDeliveryDialogComponent extends AbstractFormDialog<DeliveryDialogResultData> {
  id: number;

  formGroup = new FormGroup({
    flock: new FormControl(null),
  });

  flocks$: Observable<Flock[]> = this.store.select(getFlocks).pipe(
    map((flocks) => {
      const deliveryDate = this.data.delivery.deliveryDate;
      return flocks.filter((flock) => {
        const arrivalDate = new Date(flock.arrival.arrivalDate);
        const earliestValidDeliveryDate = new Date(arrivalDate);
        earliestValidDeliveryDate.setDate(earliestValidDeliveryDate.getDate() - 8);

        const departureDate = flock.departure?.departureDate
          ? new Date(flock.departure.departureDate)
          : null;

        return (
          earliestValidDeliveryDate <= new Date(deliveryDate) &&
          (!departureDate || new Date(deliveryDate) <= departureDate)
        );
      });
    }),
  );

  flock: Flock;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DeliveryDialogInputData,
    protected mdDialogRef: MatDialogRef<
      FlockDeliveryDialogComponent,
      AbstractDialogResultData<DeliveryDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly locale: LocaleService,
  ) {
    super();

    this.id = this.data.delivery.id;

    if (this.id && this.id > 0) {
      this.data.title = this.locale.translate('delivery.overview-table.edit-title');

      this.flocks$.pipe(take(1)).subscribe((flocks) => {
        const currentFlock = flocks.find((f) => f.id === this.data.flockId);

        if (currentFlock) {
          this.formGroup.controls.flock.setValue(currentFlock);
        }
      });
    }
  }

  save() {
    if (this.formGroup.valid) {
      const flockInfo: FlockInfo = {
        id: this.formGroup.value.flock?.id,
        name: this.formGroup.value.flock?.name,
      };

      this.mdDialogRef.close({
        status: DIALOG_RETURN_TYPES.confirm,
        result: { flock: flockInfo },
      });
    }
  }
}
